/**
 * Mode Manager
 * Orchestrates switching between Hyperliquid and Lighter trading modes
 */

class ModeManager {
    constructor() {
        this.currentMode = 'hyperliquid';  // Default mode
        this.lighterSDK = null;
        this.lighterGlobalDataManager = null;
        this.isLighterInitialized = false;
        this.lighterSDKLoaded = false;  // Track if SDK modules are loaded
        
        // console.log('[MODE-MANAGER] Initialized');
    }
    
    /**
     * Dynamically load Lighter SDK modules
     */
    async loadLighterSDKModules() {
        if (this.lighterSDKLoaded) {
            // console.log('[MODE-MANAGER] Lighter SDK modules already loaded');
            return;
        }
        
        try {
            // console.log('[MODE-MANAGER] 📦 Loading Lighter SDK modules...');
            
            // Dynamic imports
            const { LighterSDK } = await import('/lightersdk/sdk/core/lighter-sdk.js');
            const { LighterAPI } = await import('/lightersdk/sdk/core/lighter-api.js');
            const { GlobalDataManager } = await import('/lighter/managers/global-data-manager.js');
            
            // Expose to window
            window.LighterSDK = LighterSDK;
            window.LighterAPI = LighterAPI;
            window.GlobalDataManager = GlobalDataManager;
            
            this.lighterSDKLoaded = true;
            // console.log('[MODE-MANAGER] ✅ Lighter SDK modules loaded');
        } catch (error) {
            console.error('[MODE-MANAGER] ❌ Failed to load Lighter SDK modules:', error);
            throw error;
        }
    }
    
    /**
     * Initialize mode manager
     * Called after app authentication
     */
    async init() {
        // console.log('[MODE-MANAGER] Setting up mode switcher UI');
        this.setupModeSwitcherEvents();
        
        // Pre-initialize Lighter SDK in background if credentials exist
        this.preInitializeLighterSDK();
    }
    
    /**
     * Pre-initialize Lighter SDK in background
     * Non-blocking - runs after app start to prepare Lighter mode
     * @param {boolean} forceReinit - Force reinitialization even if already initialized
     */
    async preInitializeLighterSDK(forceReinit = false) {
        try {
            const lighterCreds = window.LighterCredentialsManager?.getLighterCredentials();
            
            if (!lighterCreds || !lighterCreds.apiKey) {
                // console.log('[MODE-MANAGER] No Lighter credentials - skipping background init');
                return;
            }
            
            // Stop existing instance if reinitializing
            if (forceReinit && this.lighterGlobalDataManager) {
                // console.log('[MODE-MANAGER] 🔄 Stopping existing Lighter SDK for reinitialization...');
                this.lighterGlobalDataManager.stop();
                this.lighterGlobalDataManager = null;
                this.lighterSDK = null;
                this.isLighterInitialized = false;
            }
            
            // console.log('[MODE-MANAGER] 📦 Pre-initializing Lighter SDK in background...');
            
            // Load SDK modules
            await this.loadLighterSDKModules();
            
            // Wait for WASM to be ready
            await this.waitForLighterSDK();
            
            // Initialize SDK
            this.lighterSDK = new window.LighterSDK({
                privateKey: lighterCreds.apiKey,
                accountIndex: lighterCreds.accountIndex,
                apiKeyIndex: lighterCreds.apiKeyIndex,
                network: lighterCreds.network || 'mainnet'
            });
            
            await this.lighterSDK.init();
            
            // Start GlobalDataManager polling
            this.lighterGlobalDataManager = new window.GlobalDataManager(this.lighterSDK);
            await this.lighterGlobalDataManager.start();
            
            this.isLighterInitialized = true;
            // console.log('[MODE-MANAGER] ✅ Lighter SDK pre-initialized successfully');
            
        } catch (error) {
            console.warn('[MODE-MANAGER] Lighter background init failed (non-critical):', error);
        }
    }
    
    /**
     * Setup mode switcher button events
     */
    setupModeSwitcherEvents() {
        // Handle top overlay mode tabs (if present)
        const modeTabs = document.querySelectorAll('.mode-tab');
        modeTabs.forEach(tab => {
            tab.addEventListener('click', async () => {
                const mode = tab.dataset.mode;
                await this.switchMode(mode);
            });
        });
        
        // Handle sidebar mode buttons
        const sidebarModeBtns = document.querySelectorAll('.sidebar-mode-btn');
        sidebarModeBtns.forEach(btn => {
            btn.addEventListener('click', async () => {
                const mode = btn.dataset.mode;
                await this.switchMode(mode);
            });
        });
        
        // console.log('[MODE-MANAGER] Mode switcher events registered');
    }
    
    /**
     * Switch between Hyperliquid and Lighter modes
     * @param {string} newMode - 'hyperliquid' or 'lighter'
     */
    async switchMode(newMode) {
        if (this.currentMode === newMode) {
            // console.log('[MODE-MANAGER] Already in', newMode, 'mode');
            return;
        }
        
        // console.log(`[MODE-MANAGER] Switching from ${this.currentMode} to ${newMode} mode`);
        
        const previousMode = this.currentMode;
        this.currentMode = newMode;
        
        // Update UI immediately
        this.updateModeTabs(newMode);
        
        if (newMode === 'lighter') {
            await this.activateLighterMode();
        } else {
            await this.activateHyperliquidMode();
        }
        
        // console.log(`[MODE-MANAGER] ✅ ${newMode} mode activated`);
    }
    
    /**
     * Update mode tab active states
     */
    updateModeTabs(activeMode) {
        // Update top overlay tabs (if present)
        document.querySelectorAll('.mode-tab').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.mode === activeMode);
        });
        
        // Update sidebar mode buttons
        document.querySelectorAll('.sidebar-mode-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.mode === activeMode);
        });
    }
    
    /**
     * Activate Lighter mode
     */
    async activateLighterMode() {
        // console.log('[MODE-MANAGER] 🔥 Activating Lighter mode...');
        
        // Initialize Lighter SDK if not already done
        if (!this.isLighterInitialized) {
            const initialized = await this.initializeLighterSDK();
            if (!initialized) {
                // console.log('[MODE-MANAGER] Lighter not configured, redirecting to settings');
                
                // Update mode and sidebar for Lighter
                this.currentMode = 'lighter';
                this.updateSidebarForLighter();
                
                // Navigate to Lighter settings so user can configure credentials
                if (window.NavigationManager) {
                    window.NavigationManager.switchView('lighter-settings');
                }
                
                // Show notification
                if (window.NotificationManager) {
                    window.NotificationManager.showSimpleNotification(
                        'Please configure your Lighter credentials to continue',
                        'info',
                        5000
                    );
                }
                
                return;
            }
        }
        
        // Step 2: Stop view-specific Hyperliquid services
        await this.stopViewSpecificServices();
        
        // Step 3: Update sidebar to show Lighter tabs
        this.updateSidebarForLighter();
        
        // Step 4: Navigate to Lighter dashboard
        if (window.NavigationManager) {
            window.NavigationManager.switchView('lighter-dashboard');
        }
        
        // Step 5: Dispatch mode-changed event for sidebar and other components
        window.dispatchEvent(new CustomEvent('mode-changed', { 
            detail: { mode: 'lighter' } 
        }));
        
        // console.log('[MODE-MANAGER] ✅ Lighter mode fully activated');
    }
    
    /**
     * Activate Hyperliquid mode
     */
    async activateHyperliquidMode() {
        // console.log('[MODE-MANAGER] ⚡ Activating Hyperliquid mode...');
        
        // Step 1: Stop Lighter polling (but keep SDK initialized for fast switching back)
        if (this.lighterGlobalDataManager) {
            this.lighterGlobalDataManager.stop();
        }
        
        // Step 2: Update sidebar to show Hyperliquid tabs
        this.updateSidebarForHyperliquid();
        
        // Step 3: Navigate to Hyperliquid dashboard
        if (window.NavigationManager) {
            window.NavigationManager.switchView('dashboard');
        }
        
        // Step 4: Dispatch mode-changed event for sidebar and other components
        window.dispatchEvent(new CustomEvent('mode-changed', { 
            detail: { mode: 'hyperliquid' } 
        }));
        
        // console.log('[MODE-MANAGER] ✅ Hyperliquid mode fully activated');
    }
    
    /**
     * Initialize Lighter SDK
     */
    async initializeLighterSDK() {
        // console.log('[MODE-MANAGER] 🔧 Initializing Lighter SDK...');
        
        // Load SDK modules if not already loaded
        await this.loadLighterSDKModules();
        
        // Get Lighter-specific credentials
        const lighterCreds = window.LighterCredentialsManager?.getLighterCredentials();
        
        // Check if credentials exist
        if (!lighterCreds || !lighterCreds.apiKey) {
            console.warn('[MODE-MANAGER] No Lighter credentials found');
            
            // Navigate to settings to configure
            if (window.NavigationManager) {
                window.NavigationManager.switchView('settings');
            }
            
            // Show notification
            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'info',
                    title: 'Lighter Not Configured',
                    message: 'Please add your Lighter credentials in the Settings page.',
                    duration: 5000
                });
            }
            
            // Return without throwing - let user configure
            return false;
        }
        
        // Wait for LighterSDK to be loaded
        if (!window.LighterSDK) {
            console.warn('[MODE-MANAGER] LighterSDK not loaded yet, waiting...');
            await this.waitForLighterSDK();
        }
        
        // Initialize Lighter SDK with correct credentials
        this.lighterSDK = new window.LighterSDK({
            privateKey: lighterCreds.apiKey,
            accountIndex: lighterCreds.accountIndex,
            apiKeyIndex: lighterCreds.apiKeyIndex,
            network: 'mainnet'
        });
        
        await this.lighterSDK.init();
        // console.log('[MODE-MANAGER] ✅ Lighter SDK initialized');
        
        // Initialize GlobalDataManager
        this.lighterGlobalDataManager = new window.GlobalDataManager(this.lighterSDK);
        await this.lighterGlobalDataManager.start();
        // console.log('[MODE-MANAGER] ✅ Lighter GlobalDataManager started');
        
        this.isLighterInitialized = true;
        return true;
    }
    
    /**
     * Stop view-specific Hyperliquid services
     * Keep positions/account tracking running in background
     */
    async stopViewSpecificServices() {
        // console.log('[MODE-MANAGER] 🛑 Stopping view-specific Hyperliquid services...');
        
        // Stop orderbook WebSocket if running
        if (window.BrowserOrderbookTracker?.isRunning) {
            // console.log('[MODE-MANAGER] Stopping orderbook WebSocket');
            await window.BrowserOrderbookTracker.stop();
        }
        
        // Stop candle WebSocket if running
        if (window.BrowserCandleTracker?.isRunning) {
            // console.log('[MODE-MANAGER] Stopping candle WebSocket');
            await window.BrowserCandleTracker.stop();
        }
        
        // DO NOT stop position tracker - keep running
        // console.log('[MODE-MANAGER] ✅ Hyperliquid position/account tracking continues in background');
    }
    
    /**
     * Update sidebar for Lighter mode
     */
    updateSidebarForLighter() {
        // console.log('[MODE-MANAGER] 🎨 Updating sidebar for Lighter mode');
        
        if (window.LeftSidebar) {
            window.LeftSidebar.refreshForMode();
        }
    }
    
    /**
     * Update sidebar for Hyperliquid mode
     */
    updateSidebarForHyperliquid() {
        // console.log('[MODE-MANAGER] 🎨 Updating sidebar for Hyperliquid mode');
        
        if (window.LeftSidebar) {
            window.LeftSidebar.refreshForMode();
        }
    }
    
    /**
     * Notify DashboardView of mode change
     */
    notifyDashboardModeChange(mode) {
        // console.log(`[MODE-MANAGER] Notifying dashboard of mode change: ${mode}`);
        
        // Get DashboardView instance
        const dashboardView = window.NavigationManager?.viewInstances?.get('dashboard');
        
        if (dashboardView) {
            if (mode === 'lighter') {
                // Switch to Lighter data
                dashboardView.cleanupLighterDataListener();
                dashboardView.setupLighterDataListener();
            } else {
                // Switch back to Hyperliquid data
                dashboardView.cleanupLighterDataListener();
            }
        }
    }
    
    /**
     * Notify TerminalView of mode change
     */
    notifyTerminalModeChange(mode) {
        const terminalView = window.NavigationManager?.viewInstances?.get('terminal');
        
        if (terminalView && window.NavigationManager?.currentView === 'terminal') {
            // Terminal is currently active - trigger re-initialization
            // console.log(`[MODE-MANAGER] Re-initializing terminal for ${mode} mode`);
            terminalView.onHide();
            terminalView.onShow();
        }
    }
    
    /**
     * Wait for LighterSDK to be loaded (triggers lazy WASM load if needed)
     */
    async waitForLighterSDK(timeout = 10000) {
        // Trigger WASM loading if not already loaded
        if (window.loadLighterWASM && !window._lighterWasmReady) {
            // console.log('[MODE-MANAGER] Triggering lazy WASM load...');
            await window.loadLighterWASM();
        }
        
        const startTime = Date.now();
        
        while (!window.LighterSDK || !window.GlobalDataManager) {
            if (Date.now() - startTime > timeout) {
                throw new Error('Timeout waiting for Lighter SDK to load');
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // console.log('[MODE-MANAGER] ✅ Lighter SDK loaded');
    }
}

// Create singleton instance
const modeManager = new ModeManager();
window.ModeManager = modeManager;

export { ModeManager, modeManager as default };
